import {
  Box,
  Popover,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  useMediaQuery,
} from "@mui/material";
import UIButton from "components/ui/form/UIButton";
import UIDatePicker from "components/datepicker/UIDatePicker";
import { useForm, Controller } from "react-hook-form";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { formatToE164, FormError, genderOptionList, isCountryCode } from "config/helpers/common_helper";
import { useEffect, useState } from "react";
import { parseName } from "humanparser";
import { formatDate } from "config/helpers/date_helper";
import { CREATE_AUDIT_EVENT, UPDATE_PATIENT } from "adapters/ehr.service";
import { toast } from "react-toastify";
import { patientObject } from "redux/actions/PatientActions";
import { useDispatch, useSelector } from "react-redux";
import { isValidPhoneNumber } from "libphonenumber-js";
import UIPhoneNumber from "components/ui/textField/UIPhoneNumber";
import UITextField from "components/ui/textField/UITextField";
import { useCopilotEditPatientActions } from "components/NokiCopilot/useCopilotEditPatientActions";

const PatientUpdateDrawer = ({
  setIsEditPatientDirty = () => { },
  ...props
}) => {
  const reduxDispatch = useDispatch();
  const { i18n } = useTranslation();
  const isMobile = useMediaQuery("(max-width:767px)");

  const telecom = props?.patientObj?.telecom || [];

  const email = telecom.find((t) => t.system === "email")?.value || "";
  const phone = telecom.find((t) => t.system === "phone")?.value || "+1";

  const formInitialValues = {
    patientName: props?.patientObj?.name?.[0]?.text || "",
    dob: props?.patientObj?.birthDate
      ? dayjs(props?.patientObj?.birthDate)
      : null,
    gender: props?.patientObj?.gender || "",
    patientEmail: email,
    patientPhone: phone,
  };
  const theme = useSelector((state) => state?.app.theme);

  const formData = useForm({
    defaultValues: formInitialValues,
    shouldFocusError: true,
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    reset,
    setValue,
    clearErrors,
  } = props?.formControlObj || formData;

  const PractitionerID = useSelector(
    (state) => state?.auth?.practitionerData?.id
  );
  const formCurrentValues = watch();

  const hasFormValuesChanged = () => {
    const initialValuesString = JSON.stringify(formInitialValues);
    const currentValuesString = JSON.stringify(formCurrentValues);

    return initialValuesString !== currentValuesString;
  };

  const [isLoading, setIsLoading] = useState({ saveBtn: false });
  const isTab = useMediaQuery(
    (theme) => theme.breakpoints.up("sm") && theme.breakpoints.down("lg")
  );

  useEffect(() => {
    setIsEditPatientDirty?.(isDirty);
  }, [isDirty]);

  const handleOnSavePatient = async (data) => {
    if (!hasFormValuesChanged()) {
      // Display toast notification indicating no changes were made
      toast.warning(`${i18n.t("Patient_Screen.NO_CHANGES_TOAST")}`, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 4000,
      });
      return; // Exit early if no changes were detected
    }
    setIsLoading(true);

    // Create a deep copy of props.patientObj to avoid mutating the original state
    let payload = JSON.parse(JSON.stringify(props.patientObj));

    if (Object.hasOwn(payload, "encounterReset")) {
      delete payload.encounterReset;
    }

    const attrs = parseName(data?.patientName);
    const { salutation, firstName, middleName, lastName, suffix } = attrs;

    const givenNames = middleName ? [firstName, middleName] : [firstName];

    const nameList = [
      {
        use: "official",
        family: lastName,
        given: givenNames,
        prefix: salutation ? [salutation.trim()] : [],
        suffix: suffix ? [suffix.trim()] : [],
        text: data?.patientName?.trim(),
      },
    ];

    if (data?.patientName) {
      payload.name = nameList;
    }
    // debugger;
    if (data?.dob) {
      payload.birthDate = formatDate(data?.dob, "-", "YYYY-MM-DD");
    } else {
      delete payload.birthDate;
    }

    if (data?.gender) {
      payload.gender = data?.gender;
    }

    delete payload.clinicalImpressionReset;

    const telecom = [];

    if (data?.patientEmail) {
      telecom.push({
        system: "email",
        value: data.patientEmail.trim(),
        use: "home",
      });
    }

    if (data?.patientPhone && formatToE164(data.patientPhone.trim())) {
      telecom.push({
        system: "phone",
        value: formatToE164(data.patientPhone.trim()),
        use: "mobile",
      });
    }

    if (telecom.length > 0) {
      payload.telecom = telecom;
    } else {
      delete payload.telecom;
    }

    try {
      setIsLoading({ saveBtn: true });
      const response = await UPDATE_PATIENT({
        resourceType: "Patient",
        ...payload,
      });
      CREATE_AUDIT_EVENT({
        practitionerID: `Practitioner/${PractitionerID}`,
        patientName: payload.name[0].text,
        type: { display: "Update", code: "110121" },
        // reason: `Patient Modified: ${payload?.name?.[0]?.text}`,
        reason: JSON.stringify({
          msg_code: "PATIENT_MODIFIED",
          data: payload?.name?.[0]?.text
        })
      });

      toast.success(i18n.t("common.PATIENT_UPDATED_SUCCESSFULLY"));
      reduxDispatch(patientObject(response?.data));
      if (props?.rendererFunction) {
        props?.rendererFunction();
      }
    } catch (error) {
      console.error("Failed to update patient", error);
      toast.error("Failed to update patient");
    } finally {
      props?.close({ directCancel: true });
      reset({ ...data });
    }
  };

  const isNotEmpty = (value) => value?.trim() !== "";

  // Voice: fill fields / Update / Cancel on the Edit Patient popup.
  // Registered here so it mounts only while the popup is open (naturally
  // scoped). Reuses the same form, validation, update + cancel handlers.
  useCopilotEditPatientActions({
    setValue,
    clearErrors,
    initialName: props?.patientObj?.name?.[0],
    submit: () => handleSubmit(handleOnSavePatient)(),
    cancel: () => props?.close({ directCancel: true }),
  });

  return (
    <>
      <form
        // sx={{
        //   display: "flex",
        //   flexDirection: "column",
        //   gap: "16px",
        // }}
        onSubmit={handleSubmit(handleOnSavePatient)}
        className='d-flex flex-column gap-3'
      >
        <Typography variant="h5">{i18n.t("common.EDIT")}</Typography>
        {/* <div>
          <Controller
            control={control}
            name="patientName"
            rules={{
              required: i18n.t(
                "patient2.PATIENT_FIRST_NAME_LAST_NAME_IS_REQUIRED"
              ),
              validate: {
                isNotEmpty: (value) =>
                  isNotEmpty(value) ||
                  i18n.t("patient2.PATIENT_FIRST_NAME_LAST_NAME_IS_REQUIRED"),
                isAlphabetical: (value) =>
                  /^(?=\p{L})[\p{L}0-9., ]*$/u.test(value) ||
                  i18n.t("error_messages.INVALID_NAME"), // Add an error message for invalid name
              },
            }}
            render={({ field }) => (
              <UITextField
                {...field}
                autoFocus={!isTab}
                // autoFocus
                autoComplete="off"
                fieldLabel={i18n.t(
                  "Patient_Screen.PATIENT_FIRST_NAME_LAST_NAME"
                )}
                placeholder={i18n.t(
                  "Patient_Screen.PATIENT_FIRST_NAME_LAST_NAME"
                )}
                variant="outlined"
                fullWidth
                size="small"
                error={errors?.patientName?.message}
                sx={{
                  "& .MuiInputBase-input::placeholder": {
                    opacity: 0.8,
                    fontWeight: 500,
                    color: theme === "dark" ? "#aaa" : "#aaa",
                  },
                  // Style for the asterisk
                  "& .MuiInputBase-root": {
                    "&::after": {
                      content: `'${field?.value ? "" : "*"}'`,
                      color: "red", // Set asterisk color to red
                      position: "absolute",
                      left: "5px",
                    },
                  },
                  fontSize: isMobile ? "1.2vh" : "",
                }}
              />
            )}
          />
          <FormError
            sx={{ fontSize: isMobile ? "1.2vh" : "" }}
            error={errors?.patientName?.message}
          />
        </div> */}
        {/* <TextFieldWithTopLabelComponent
                        {...PatientNameValidationObject}
                        onChange={handleOnChange}
                        width={"100%"}
                        sx={{
                          ".MuiInputBase-root": {
                            borderRadius: "8px",
                          },
                        }}
                      /> */}
        <Grid
          container
          spacing={1}
        // className="d-flex gap-2 align-items-start"
        >
          <Grid
            // sx={{ marginTop: isMobile ? "-1.2vh" : "" }}
            item
            xs={isMobile ? 12 : 12}
          >
            <Controller
              control={control}
              name="patientName"
              rules={{
                required: i18n.t(
                  "patient2.PATIENT_FIRST_NAME_LAST_NAME_IS_REQUIRED"
                ),
                validate: {
                  isNotEmpty: (value) =>
                    isNotEmpty(value) ||
                    i18n.t("patient2.PATIENT_FIRST_NAME_LAST_NAME_IS_REQUIRED"),
                  isAlphabetical: (value) =>
                    /^(?=\p{L})[\p{L}0-9., ]*$/u.test(value) ||
                    i18n.t("error_messages.INVALID_NAME"), // Add an error message for invalid name
                },
              }}
              render={({ field }) => (
                <UITextField
                  {...field}
                  autoFocus={!isTab}
                  // autoFocus
                  autoComplete="off"
                  required
                  fieldLabel={i18n.t(
                    "Patient_Screen.PATIENT_FIRST_NAME_LAST_NAME"
                  )}
                  placeholder={i18n.t(
                    "Patient_Screen.PATIENT_FIRST_NAME_LAST_NAME"
                  )}
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={errors?.patientName?.message}
                  inputRef={field?.ref}
                  sx={{
                    // "& .MuiInputBase-input::placeholder": {
                    //   opacity: 0.8,
                    //   fontWeight: 500,
                    //   color: theme === "dark" ? "#aaa" : "#aaa",
                    // },
                    // // Style for the asterisk
                    // "& .MuiInputBase-root": {
                    //   "&::after": {
                    //     content: `'${field?.value ? "" : "*"}'`,
                    //     color: "red", // Set asterisk color to red
                    //     position: "absolute",
                    //     left: "5px",
                    //   },
                    // },
                    fontSize: isMobile ? "1.2vh" : "",
                  }}
                />
              )}
            />
            <FormError
              sx={{ fontSize: isMobile ? "1.2vh" : "" }}
              error={errors?.patientName?.message}
            />
          </Grid>
          <Grid
            // sx={{ marginTop: isMobile ? "-1.2vh" : "" }}
            item
            xs={isMobile ? 12 : 12}
          >
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <div>
                  <div className="fxs b-500">
                    {i18n.t("Patient_Screen.GENDER")}
                    <span className="text-danger" style={{ fontSize: '14px' }}>
                      *
                    </span>
                  </div>
                  <Select
                    fullWidth
                    {...field}
                    size="small"
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    sx={{
                      "& .MuiInputBase-input::placeholder": {
                        color: "black",
                        opacity: 0.8,
                      },
                      // Style for the asterisk
                      "& .MuiInputBase-input": {
                        "&::after": {
                          content: `'${field?.value ? "" : i18n.t("Patient_Screen.GENDER")
                            }'`,
                          // color: "red", // Set asterisk color to red
                          position: "absolute",
                          left: "10px",
                        },
                      },
                      fontSize: isMobile ? "1.2vh" : "",
                    }}
                    ref={field?.ref}
                  >
                    {genderOptionList?.map((item, id) => (
                      <MenuItem
                        value={item.value}
                        key={id}
                        title={i18n.t(item.label)}
                      >
                        {i18n.t(item.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )}
            />
          </Grid>
          <Grid
            item
            // sx={{ marginTop: isMobile ? "-1.2vh" : "" }}
            sm={isMobile ? 12 : 12}
            xs={isMobile ? 12 : 12}
          >
            <Controller
              control={control}
              name="dob"
              rules={{
                required: i18n.t("error_messages.DOB_REQUIRED"),
                validate: (value) => {
                  const inputDate = dayjs(value).startOf("day");

                  if (!inputDate.isValid()) {
                    return i18n.t("patient2.INVALID_DATE_FORMAT");
                  }

                  const maxDate = dayjs().startOf("day");
                  if (inputDate.isAfter(maxDate)) {
                    return i18n.t("patient2.DOB_CANNOT_BE_FUTURE_DATE");
                  }

                  const minDate = dayjs("1900-01-01").startOf("day");
                  if (inputDate.isBefore(minDate)) {
                    return i18n.t("patient2.DOB_CANNOT_BE_BEFORE_1900");
                  }

                  return true;
                }

              }}
              render={({ field }) => (
                <UIDatePicker
                  {...field}
                  maxDate={dayjs()}
                  minDate={dayjs('1900-01-01')}
                  fieldLabel={i18n.t("Patient_Screen.DATE_OF_BIRTH")}
                  placeholder={i18n.t("Patient_Screen.DATE_OF_BIRTH")}
                  size={"small"}
                  sx={{
                    // "& .MuiInputBase-root": {
                    //   "&::after": {
                    //     content: `'${field?.value ? "" : "*"}'`,
                    //     color: "red", // Set asterisk color to red
                    //     position: "absolute",
                    //     left: "5px",
                    //   },
                    // },
                  }}
                  inputRef={field?.ref}
                  error={errors.dob}
                  fullWidth
                  placement={isMobile ? "auto" : "left"}
                />
              )}
            />
            <FormError
              sx={{ fontSize: isMobile ? "1.2vh" : "" }}
              error={errors?.dob?.message}
            />
          </Grid>
          <Grid
            item
            // sm={isMobile ? 12 : 6}
            // xs={isMobile ? 12 : 6}
            sm={12}
            xs={12}
          >
            <Controller
              control={control}
              name="patientPhone"
              defaultValue={''}
              rules={{
                validate: (value) => {
                  if (isCountryCode(value) || value === '+' || !value) {
                    return true
                  }
                  return !!formatToE164(value) || i18n.t("error_messages.INVALID_PHONE")
                }
              }}
              render={({ field }) => (
                <UIPhoneNumber
                  // label={`${i18n.t("ehr.personalInfo.CELL_PHONE")}*`}
                  {...field}
                  fieldLabel={i18n.t("ehr.personalInfo.CELL_PHONE")}
                  defaultCountry="us"
                  fullWidth
                  value={field.value}
                  onChange={(value) => {
                    let cleaned = value.replace(/[()\s-]/g, '');
                    field.onChange(cleaned)
                  }}
                  onFocus={() => { }}
                  error={!!errors?.patientPhone}
                  sx={{
                    ".MuiInputLabel-root": {
                      zIndex: 0, // Make sure the label stays above the input
                    },
                  }}
                  inputProps={{
                    ref: field?.ref,
                  }}
                // helperText={errors?.patientPhone?.message}
                />
              )}
            />

            <FormError
              sx={{ fontSize: isMobile ? "1.2vh" : "" }}
              error={errors?.patientPhone?.message}
            />
          </Grid>
          <Grid
            item
            // sm={isMobile ? 12 : 6}
            // xs={isMobile ? 12 : 6}
            // sx={{ marginTop: isMobile ? "-1.2vh" : "" }}
            sm={12}
            xs={12}
          >
            <Controller
              control={control}
              name="patientEmail"
              rules={{
                maxLength: {
                  value: 100,
                  message: i18n.t("error_messages.EMAIL_MAX_LENGTH"),
                },
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: i18n.t("error_messages.INVALID_EMAIL"),
                },
              }}
              render={({ field }) => (
                <UITextField
                  fieldLabel={
                    i18n.t("dashboard2.EMAIL")
                  }
                  {...field}
                  autoComplete="off"
                  placeholder={i18n.t("dashboard2.EMAIL")}
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={!!errors?.patientEmail}
                  helperText={errors?.patientEmail?.message}
                  inputRef={field?.ref}
                  sx={{
                    "& .MuiInputBase-input::placeholder": {
                      opacity: 1,
                      fontWeight: 500,
                      color: theme === "dark" ? "#aaa" : "#aaa",
                    },
                    fontSize: isMobile ? "1.2vh" : "",
                  }}
                />
              )}
            />

          </Grid>
        </Grid>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            gap: "10px",
          }}
        >
          <UIButton
            fullWidth
            callback={handleSubmit(handleOnSavePatient)}
            variant="contained"
            text={i18n.t("common.UPDATE")}
            loading={isLoading?.saveBtn}
            type='submit'
          // loadingIndicator={"Creating patient..."}
          />

          <UIButton
            fullWidth
            callback={() => {
              props?.close({ directCancel: true });
              setIsEditPatientDirty?.(false);
            }}
            variant="outlined"
            text={i18n.t("Patient_Screen.CANCEL")}
          // loading={isLoading}
          // loadingIndicator={"Creating patient..."}
          />
        </Box>
      </form>
    </>
  );
};

export default PatientUpdateDrawer;
